class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.phDesktop = window.matchMedia('(min-width: 990px)');
    this.phHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  }

  connectedCallback() {
    if (!this.hasAttribute('data-ph-desktop-open') || this.phController) return;
    this.phController = new AbortController();
    const options = { signal: this.phController.signal };
    this.addEventListener('pointerenter', this.phPointerEnter.bind(this), options);
    this.addEventListener('pointerleave', this.phPointerLeave.bind(this), options);
    this.addEventListener('pointerdown', this.phPointerDown.bind(this), options);
    this.addEventListener('focusin', this.phFocusIn.bind(this), options);
    this.addEventListener('keydown', this.phKeyDown.bind(this), options);
    document.addEventListener('pointermove', this.phPointerMove.bind(this), options);
    this.phDesktop.addEventListener('change', () => {
      this.phClearTimers();
      this.phHovered = false;
      this.phBridge = false;
      this.phKeyboardFocus = false;
      if (!this.phDesktop.matches) this.close();
    }, options);
  }

  disconnectedCallback() {
    this.phClearTimers();
    this.phController?.abort();
    this.phController = null;
  }

  phEnabled() {
    return this.hasAttribute('data-ph-desktop-open') && this.phDesktop.matches;
  }

  phClearTimers() {
    clearTimeout(this.phOpenTimer);
    clearTimeout(this.phCloseTimer);
    this.phOpenTimer = null;
    this.phCloseTimer = null;
  }

  phOpen() {
    if (!this.phEnabled()) return;
    this.phClearTimers();
    this.closest('.header__inline-menu')?.querySelectorAll('header-menu[data-ph-desktop-open]').forEach((menu) => {
      if (menu !== this) {
        menu.phClearTimers();
        menu.close();
      }
    });
    this.mainDetailsToggle.open = true;
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'true');
  }

  phPointerEnter(event) {
    if (!this.phEnabled() || !this.phHover.matches || event.pointerType === 'touch') return;
    this.phHovered = true;
    this.phBridge = false;
    this.phClearTimers();
    this.phOpenTimer = setTimeout(() => {
      if (this.phHovered) this.phOpen();
    }, 150);
  }

  phPointerLeave(event) {
    if (!this.phEnabled() || event.pointerType === 'touch') return;
    this.phHovered = false;
    this.phBridge = false;
    clearTimeout(this.phOpenTimer);
    this.phQueueClose();
  }

  phPointerMove(event) {
    if (!this.phEnabled() || !this.phHover.matches || this.phHovered || !this.mainDetailsToggle.open) return;
    // Treat the vertical padding between the trigger and dropdown as part of its menu area.
    const trigger = this.mainDetailsToggle.querySelector('summary').getBoundingClientRect();
    const panel = this.content.getBoundingClientRect();
    this.phBridge = event.clientX >= trigger.left && event.clientX <= trigger.right &&
      event.clientY >= trigger.bottom && event.clientY <= panel.top;
    if (this.phBridge) {
      clearTimeout(this.phCloseTimer);
      this.phCloseTimer = null;
    } else if (!this.phCloseTimer) {
      this.phQueueClose();
    }
  }

  phQueueClose() {
    clearTimeout(this.phCloseTimer);
    this.phCloseTimer = setTimeout(() => {
      this.phCloseTimer = null;
      if (this.phHovered || this.phBridge || (this.phKeyboardFocus && this.contains(document.activeElement))) return;
      this.close();
    }, 200);
  }

  phPointerDown() {
    if (!this.phEnabled()) return;
    // Pointer focus must not pre-open details and undo the native click toggle.
    this.phSkipFocus = true;
    this.phKeyboardFocus = false;
    clearTimeout(this.phOpenTimer);
    setTimeout(() => { this.phSkipFocus = false; }, 0);
  }

  phFocusIn() {
    if (!this.phEnabled() || this.phSkipFocus) return;
    this.phKeyboardFocus = true;
    this.phOpen();
  }

  phKeyDown(event) {
    if (!this.phEnabled() || event.key !== 'Escape') return;
    this.phClearTimers();
    this.phKeyboardFocus = false;
    this.phSkipFocus = true;
    this.close();
    this.mainDetailsToggle.querySelector('summary').focus();
    this.phSkipFocus = false;
    event.preventDefault();
  }

  onFocusOut() {
    if (!this.phEnabled()) return super.onFocusOut();
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.phKeyboardFocus = false;
        if (!this.phHovered && !this.phBridge) this.phQueueClose();
      }
    }, 0);
  }

  onToggle() {
    if (this.hasAttribute('data-ph-desktop-open')) {
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', String(this.mainDetailsToggle.open));
      if (!this.mainDetailsToggle.open) clearTimeout(this.phOpenTimer);
    }
    if (!this.header) return;
    this.header.preventHide = this.hasAttribute('data-ph-desktop-open')
      ? Boolean(this.header.querySelector('header-menu details[open]'))
      : this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
